import { Application, BitmapFont, BitmapText, Container, Graphics, Sprite, Text, Texture } from 'pixi.js';
import { GameMap, TILE, STATUS, SKILLS, Rng, type HeroId, type MapDescriptor, type SelfView, type SimEvent, type Snapshot, type UnitView } from '@knight/shared';
import { projTexture, projStyle, circleTexture, lightTexture, decalTexture, RARITY_COLORS } from './sprites';
import { buildHero, buildMob, groundFrame, wallTile, fogTexture, propTex, interTex, pickupTex, shadowTex, slashTex, paint, RES, DECOR_SETS, TIER_PALETTES, type PartSpec, type UnitArt } from './art';
import { prefs } from '../app/prefs';
import { isPhone } from '../app/device';

const TPS = 20;
const CHUNK = 16;

/** Positional cue passed to the audio layer; without x/y the sound plays centred at full volume. */
export interface SfxCue {
  x?: number;
  y?: number;
  vol?: number;
  throttleMs?: number;
}

const ELEMENT_TAGS = new Set(['fire', 'frost', 'lightning', 'void', 'holy', 'blight']);
const CAST_TAGS = ['fire', 'frost', 'lightning', 'void', 'holy', 'blight', 'summon', 'curse'];
const SWING_SFX: Record<string, string> = { swing: 'swing', swing_fast: 'swing_fast', swing_blunt: 'swing_blunt', swing_void: 'swing_void', swing_mob: 'swing_mob', swing_boss: 'swing_boss', bash: 'swing_blunt' };

function projSfx(visual: string, shape: string): string {
  if (visual.startsWith('arrow') || visual.startsWith('lance')) return 'bow';
  if (visual === 'fireball' || visual === 'comet' || visual === 'ember') return 'fire_shot';
  if (visual.startsWith('bolt_void') || visual === 'bolt_dark') return 'bolt_void';
  if (visual === 'bolt_storm') return 'zap';
  if (visual === 'shard_frost') return 'frost_shot';
  if (visual.startsWith('shard')) return 'shard';
  if (visual === 'spore' || visual === 'wave_water') return 'spit';
  if (visual === 'anchor') return 'swing_blunt';
  if (shape === 'blade' || shape === 'wave') return 'swing';
  return 'shoot';
}

function castSfx(skill: string): string {
  const def = SKILLS[skill];
  if (!def) return 'cast';
  const tags = def.tags as string[];
  for (const tg of CAST_TAGS) if (tags.includes(tg)) return 'cast_' + tg;
  return 'cast';
}

interface UnitSprite {
  id: number;
  view: UnitView;
  root: Container;
  fig: Container;
  hud: Container;
  parts: { spec: PartSpec; s: Sprite }[];
  art: UnitArt;
  shadow: Sprite;
  bar: Graphics;
  name: Text | null;
  status: Graphics;
  windup: Graphics;
  light: Sprite | null;
  bubble: { c: Container; until: number } | null;
  x: number;
  y: number;
  tx: number;
  ty: number;
  px: number;
  py: number;
  anim: number;
  attack: number;
  lastHp: number;
  lastMhp: number;
  lastSh: number;
  flash: number;
  seen: number;
  color: string;
  /** active transformation ('' = none); a change rebuilds the figure */
  form: string;
}

interface ProjSprite {
  id: number;
  sprite: Sprite;
  x: number;
  y: number;
  vx: number;
  vy: number;
  dieAt: number;
  trail: boolean;
  color: number;
  orbit?: { owner: number; radius: number; angVel: number; angle: number };
}

interface Particle {
  g: Sprite;
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  max: number;
  grav: number;
  shrink: boolean;
}

interface Ambient {
  s: Sprite;
  x: number;
  y: number;
  vx: number;
  vy: number;
  phase: number;
}

interface Ring {
  g: Graphics;
  x: number;
  y: number;
  r: number;
  life: number;
  max: number;
  color: number;
  fill: boolean;
}

interface Fading {
  g: Container;
  life: number;
  max: number;
  from: number;
}

interface DmgText {
  t: BitmapText;
  x: number;
  y: number;
  vy: number;
  life: number;
  max: number;
}

interface Telegraph {
  g: Graphics;
  x: number;
  y: number;
  r: number;
  x2?: number;
  y2?: number;
  kind: 'circle' | 'line';
  dieAt: number;
  bornAt: number;
}

interface Slash {
  main: Sprite;
  trail: Sprite;
  reach: number;
  ang: number;
  dir: number;
  life: number;
  max: number;
}

const STATUS_COLORS: Record<string, number> = {
  burn: 0xff7a2f,
  chill: 0x7fdcff,
  shock: 0xa8f0ff,
  blight: 0x8fd66a,
  bleed: 0xff3b5c,
  vulnerable: 0xc65cff,
  weak: 0x8a6a9a,
  haste: 0x7fffd4,
  might: 0xffb35c,
  fortify: 0xd9c9a3,
  regen: 0x7be0a0,
  shield: 0xdfe9ff,
  taunt: 0xff9a3c,
  stun: 0xffe38a,
  invuln: 0xffffff,
};

const ZONE_COLORS: Record<string, number> = {
  zone_fire: 0xff7a2f,
  zone_frost: 0x7fdcff,
  zone_holy: 0xffe38a,
  zone_blight: 0x8fd66a,
  zone_miasma: 0x5d9f3f,
  zone_storm: 0x7cc7ff,
  zone_arrows: 0xc8d6a8,
  zone_arrows_frost: 0x9fd8d8,
  zone_arrows_blight: 0x8fce6a,
  rift: 0x6a3cff,
  maelstrom: 0x3f8f9f,
  roots: 0x6f4f2a,
};

const FX_COLORS: Record<string, number> = {
  explosion_fire: 0xff7a2f,
  explosion_fire_big: 0xff4d1a,
  explosion_void: 0x8a5cff,
  burst_fire: 0xffb347,
  burst_bone: 0xefe6d2,
  nova_frost: 0x9be7ff,
  nova_shatter: 0xe0f7ff,
  pulse_frost: 0x9be7ff,
  pulse_frost_big: 0x6fc9e6,
  pulse_void: 0x6a3cff,
  aura_fire: 0xff8c42,
  aura_fire_big: 0xff5c1a,
  aura_buff: 0xffb35c,
  aura_curse: 0xc65cff,
  hex: 0xc65cff,
  hex_plague: 0x7fe07a,
  hex_gravity: 0x5a3cff,
  blood: 0xff3b5c,
  ward: 0xfff6c2,
  cry: 0xffb35c,
  terror: 0xc77dff,
  heal: 0x7be0a0,
  haste: 0x7fffd4,
  summon: 0x8a7cff,
  shrine: 0xffe38a,
  chest: 0xffd166,
  altar: 0xc0304f,
  pick: 0xffd166,
  quake: 0xb39b6a,
  bash: 0xd9c9a3,
  swing: 0xe8e8e8,
  swing_blunt: 0xa8a8a8,
  swing_fast: 0xffffff,
  swing_void: 0x9b6bff,
  swing_mob: 0xff8080,
  swing_boss: 0xff5c5c,
  chain: 0xa8f0ff,
  chain_big: 0xe8fbff,
  dash: 0xffffff,
  charge: 0xff9a3c,
  blink: 0xb0c8ff,
  levelup: 0xffd166,
};

function hex(c: string): number {
  return parseInt(c.replace('#', ''), 16);
}

function lerpColor(a: number, b: number, t: number): number {
  const ar = (a >> 16) & 255;
  const ag = (a >> 8) & 255;
  const ab = a & 255;
  const br = (b >> 16) & 255;
  const bg = (b >> 8) & 255;
  const bb = b & 255;
  return (Math.round(ar + (br - ar) * t) << 16) | (Math.round(ag + (bg - ag) * t) << 8) | Math.round(ab + (bb - ab) * t);
}

function zoneTexture(): Texture {
  return paint('zone3', 128, 128, (ctx, w, h) => {
    const g = ctx.createRadialGradient(w / 2, h / 2, 2, w / 2, h / 2, w / 2);
    g.addColorStop(0, 'rgba(255,255,255,0.55)');
    g.addColorStop(0.6, 'rgba(255,255,255,0.22)');
    g.addColorStop(1, 'rgba(255,255,255,0)');
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, w, h);
  });
}

export class GameView {
  app = new Application();
  world = new Container();
  layers = {
    ground: new Container(),
    decor: new Container(),
    lights: new Container(),
    zones: new Container(),
    units: new Container(),
    proj: new Container(),
    fx: new Container(),
    fog: new Container(),
    text: new Container(),
  };
  overlay = new Container();
  map: GameMap | null = null;
  mapDesc: MapDescriptor | null = null;
  private chunks = new Map<string, Sprite>();
  private chunkPending = new Set<string>();
  units = new Map<number, UnitSprite>();
  staticBacklog = new Map<number, Partial<UnitView>>();
  private projs = new Map<number, ProjSprite>();
  private zones = new Map<number, { g: Graphics; glow: Sprite; soft: Sprite; until: number; color: number; r: number }>();
  private pickups = new Map<number, { s: Sprite; y: number; light: Sprite | null }>();
  private inters = new Map<number, { s: Sprite; light: Sprite }>();
  private decorSprites: Sprite[] = [];
  private decorLights: Sprite[] = [];
  private fogs: { s: Sprite; vx: number; vy: number }[] = [];
  private ambient: Ambient[] = [];
  private decals: Fading[] = [];
  private afterimages: Fading[] = [];
  private telegraphs: Telegraph[] = [];
  private slashes: Slash[] = [];
  private slashDir = 1;
  private particles: Particle[] = [];
  private rings: Ring[] = [];
  private lines: Fading[] = [];
  private dmg: DmgText[] = [];
  private minimap = new Graphics();
  private vignette: Sprite | null = null;
  private lowHp: Sprite | null = null;
  private grade: Sprite | null = null;
  private gradeColor = 0x1a4a3a;
  private curTier = 1;
  myId = 0;
  myTeam = 1;
  pred = { x: 0, y: 0 };
  private renderSelf = { x: 0, y: 0 };
  private pending: { seq: number; mx: number; my: number; dash: boolean }[] = [];
  private dashLeft = 0;
  private dashDir = { x: 1, y: 0 };
  cam = { x: 0, y: 0 };
  zoom = 1.5;
  private shake = { x: 0, y: 0, power: 0 };
  serverTick = 0;
  private frame = 0;
  private ready = false;
  private destroyed = false;
  self: SelfView | null = null;
  onSfx: (kind: string, cue?: SfxCue) => void = () => {};
  /** Called a few times a second with the camera position, threat level 0..2 and current tier. */
  onListener: (x: number, y: number, threat: number, tier: number) => void = () => {};
  private lastThreat = 0;
  showMinimap = true;
  aimAngle = 0;

  async init(container: HTMLElement): Promise<void> {
    // a phone panel is often 3x: drawing every one of those pixels costs more than it shows
    const maxRes = isPhone() ? 1.5 : 2;
    await this.app.init({ resizeTo: window, background: '#07060b', antialias: true, resolution: Math.min(maxRes, window.devicePixelRatio || 1), autoDensity: true, preference: 'webgl' });
    if (this.destroyed) {
      this.app.destroy(true);
      return;
    }
    container.appendChild(this.app.canvas);
    this.app.canvas.style.display = 'block';
    for (const l of Object.values(this.layers)) this.world.addChild(l);
    this.layers.units.sortableChildren = true;
    this.app.stage.addChild(this.world);
    this.app.stage.addChild(this.overlay);
    this.buildOverlay();
    this.buildFog();
    this.buildAmbient();
    try {
      BitmapFont.install({ name: 'dmg', style: { fontFamily: 'Verdana, Arial, sans-serif', fontSize: 28, fontWeight: 'bold', fill: 0xffffff, stroke: { color: 0x000000, width: 5 } }, chars: [['0', '9'], '+-.!x'] });
    } catch (e) {
      console.warn('bitmap font', e);
    }
    this.app.ticker.add((tk) => this.update(tk.deltaMS / 1000));
    this.ready = true;
  }

  private buildOverlay(): void {
    const c = document.createElement('canvas');
    c.width = 256;
    c.height = 256;
    const ctx = c.getContext('2d')!;
    const g = ctx.createRadialGradient(128, 128, 70, 128, 128, 160);
    g.addColorStop(0, 'rgba(0,0,0,0)');
    g.addColorStop(1, 'rgba(0,0,0,0.85)');
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, 256, 256);
    this.vignette = new Sprite(Texture.from(c));
    this.vignette.eventMode = 'none';
    this.grade = new Sprite(Texture.WHITE);
    this.grade.alpha = 0.12;
    this.grade.eventMode = 'none';
    this.lowHp = new Sprite(Texture.WHITE);
    this.lowHp.tint = 0xff2030;
    this.lowHp.alpha = 0;
    this.lowHp.eventMode = 'none';
    this.overlay.addChild(this.grade, this.vignette, this.lowHp, this.minimap);
  }

  private buildFog(): void {
    const tex = fogTexture();
    for (let i = 0; i < 7; i++) {
      const s = new Sprite(tex);
      s.anchor.set(0.5);
      const size = 700 + Math.random() * 500;
      s.width = size;
      s.height = size * 0.7;
      s.alpha = 0.1 + Math.random() * 0.06;
      s.rotation = Math.random() * Math.PI;
      this.layers.fog.addChild(s);
      this.fogs.push({ s, vx: 5 + Math.random() * 10, vy: (Math.random() - 0.5) * 6 });
    }
  }

  private buildAmbient(): void {
    const tex = circleTexture(2, '#ffffff');
    for (let i = 0; i < 60; i++) {
      const s = new Sprite(tex);
      s.anchor.set(0.5);
      s.blendMode = 'add';
      this.layers.fog.addChild(s);
      this.ambient.push({ s, x: 0, y: 0, vx: 0, vy: 0, phase: Math.random() * 10 });
    }
  }

  destroy(): void {
    this.destroyed = true;
    if (this.ready) this.app.destroy(true, { children: true });
  }

  setMap(desc: MapDescriptor): void {
    if (this.mapDesc && this.mapDesc.seed === desc.seed && this.mapDesc.kind === desc.kind) return;
    this.mapDesc = desc;
    this.map = GameMap.fromDescriptor(desc);
    for (const s of this.chunks.values()) s.destroy();
    this.chunks.clear();
    this.layers.ground.removeChildren();
    this.clearEntities();
    this.buildDecor();
  }

  clearEntities(): void {
    for (const u of this.units.values()) this.destroyUnit(u);
    this.units.clear();
    for (const p of this.projs.values()) p.sprite.destroy();
    this.projs.clear();
    for (const z of this.zones.values()) {
      z.g.destroy();
      z.glow.destroy();
      z.soft.destroy();
    }
    this.zones.clear();
    for (const p of this.pickups.values()) {
      p.s.destroy();
      p.light?.destroy();
    }
    this.pickups.clear();
    for (const i of this.inters.values()) {
      i.s.destroy();
      i.light.destroy();
    }
    this.inters.clear();
    for (const t of this.telegraphs) t.g.destroy();
    this.telegraphs = [];
    for (const s of this.slashes) {
      s.main.destroy();
      s.trail.destroy();
    }
    this.slashes = [];
    for (const p of this.particles) p.g.destroy();
    this.particles = [];
    for (const r of this.rings) r.g.destroy();
    this.rings = [];
    for (const l of this.lines) l.g.destroy();
    this.lines = [];
    for (const d of this.dmg) d.t.destroy();
    this.dmg = [];
    for (const d of this.decals) d.g.destroy();
    this.decals = [];
    for (const a of this.afterimages) a.g.destroy();
    this.afterimages = [];
    this.pending = [];
  }

  private destroyUnit(u: UnitSprite): void {
    u.light?.destroy();
    u.shadow.destroy();
    u.root.destroy({ children: true });
  }

  resetSelf(x: number, y: number): void {
    this.pred = { x, y };
    this.renderSelf = { x, y };
    this.cam = { x, y };
    this.pending = [];
    for (const f of this.fogs) {
      f.s.position.set(x + (Math.random() - 0.5) * 2400, y + (Math.random() - 0.5) * 1600);
    }
  }

  // ------------------------------------------------------------------ scenery

  private buildDecor(): void {
    for (const s of this.decorSprites) s.destroy();
    for (const l of this.decorLights) l.destroy();
    this.decorSprites = [];
    this.decorLights = [];
    const map = this.map;
    if (!map) return;
    const rng = new Rng(map.seed ^ 0xdec0);
    const realm = map.kind === 'realm';
    const count = realm ? 460 : 36;
    let tries = 0;
    while (this.decorSprites.length < count && tries++ < count * 6) {
      const x = rng.float(80, map.widthPx - 80);
      const y = rng.float(80, map.heightPx - 80);
      if (!map.clear(x, y, 12)) continue;
      if (realm) {
        const dc = Math.hypot(x - map.center.x, y - map.center.y);
        if (dc < map.arenaRadius + 260) continue;
        if (map.spawnPoints.some((p) => Math.hypot(p.x - x, p.y - y) < 200)) continue;
      }
      const tier = realm ? map.tierAt(x, y) : 2;
      const set = DECOR_SETS[tier] ?? DECOR_SETS[1];
      const d = set[rng.weightedIndex(set.map((k) => k.weight))];
      const variant = rng.int(0, 3);
      const sp = new Sprite(propTex(d.kind, tier, variant));
      sp.anchor.set(0.5, 0.96);
      const sc = (d.scale / RES) * rng.float(0.9, 1.15);
      sp.scale.set(rng.chance(0.5) ? -sc : sc, sc);
      sp.position.set(Math.round(x), Math.round(y));
      if (d.tall) {
        sp.zIndex = y - 2;
        this.layers.units.addChild(sp);
      } else this.layers.decor.addChild(sp);
      this.decorSprites.push(sp);
      if (d.light) {
        const l = this.makeLight(d.light.radius, d.light.color, 0.3);
        l.position.set(x, y - 10);
        this.decorLights.push(l);
      }
    }
  }

  private ensureChunk(cx: number, cy: number): void {
    const key = cx + ':' + cy;
    if (this.chunks.has(key) || this.chunkPending.has(key) || !this.map) return;
    this.chunkPending.add(key);
    const map = this.map;
    const cont = new Container();
    for (let ty = 0; ty < CHUNK; ty++) {
      for (let tx = 0; tx < CHUNK; tx++) {
        const gx = cx * CHUNK + tx;
        const gy = cy * CHUNK + ty;
        if (gx >= map.w || gy >= map.h) continue;
        const solid = map.isSolidTile(gx, gy);
        const tier = map.tiers[map.idx(gx, gy)] || 1;
        let s: Sprite;
        if (solid) {
          const mask = (map.isSolidTile(gx, gy - 1) ? 0 : 1) | (map.isSolidTile(gx + 1, gy) ? 0 : 2) | (map.isSolidTile(gx, gy + 1) ? 0 : 4) | (map.isSolidTile(gx - 1, gy) ? 0 : 8);
          s = new Sprite(wallTile(tier, mask, (gx * 5 + gy * 3) % 4));
          s.scale.set(1 / RES);
        } else {
          s = new Sprite(groundFrame(tier, (gx * 7 + gy * 13) % 8, (gx * 3 + gy * 5) % 8));
        }
        s.position.set(tx * TILE, ty * TILE);
        cont.addChild(s);
        if (!solid && map.isSolidTile(gx, gy - 1)) {
          const sh = new Graphics().rect(tx * TILE, ty * TILE, TILE, 8).fill({ color: 0x000000, alpha: 0.4 });
          cont.addChild(sh);
        }
      }
    }
    const tex = this.app.renderer.generateTexture({ target: cont, resolution: 1 });
    cont.destroy({ children: true });
    const sprite = new Sprite(tex);
    sprite.position.set(cx * CHUNK * TILE, cy * CHUNK * TILE);
    this.layers.ground.addChild(sprite);
    this.chunks.set(key, sprite);
    this.chunkPending.delete(key);
  }

  private updateChunks(): void {
    if (!this.map) return;
    const w = this.app.screen.width / this.zoom;
    const h = this.app.screen.height / this.zoom;
    const x0 = Math.floor((this.cam.x - w / 2) / (CHUNK * TILE)) - 1;
    const x1 = Math.floor((this.cam.x + w / 2) / (CHUNK * TILE)) + 1;
    const y0 = Math.floor((this.cam.y - h / 2) / (CHUNK * TILE)) - 1;
    const y1 = Math.floor((this.cam.y + h / 2) / (CHUNK * TILE)) + 1;
    const maxC = Math.ceil(this.map.w / CHUNK);
    for (let cy = Math.max(0, y0); cy <= Math.min(maxC - 1, y1); cy++) {
      for (let cx = Math.max(0, x0); cx <= Math.min(maxC - 1, x1); cx++) this.ensureChunk(cx, cy);
    }
    for (const [key, s] of this.chunks) {
      const [cx, cy] = key.split(':').map(Number);
      s.visible = cx >= x0 && cx <= x1 && cy >= y0 && cy <= y1;
    }
  }

  private makeLight(radius: number, color: number, alpha: number): Sprite {
    const s = new Sprite(lightTexture(256));
    s.anchor.set(0.5);
    s.width = radius * 2;
    s.height = radius * 2;
    s.tint = color;
    s.alpha = alpha;
    s.blendMode = 'add';
    this.layers.lights.addChild(s);
    return s;
  }

  // ------------------------------------------------------------------ snapshots

  applySnapshot(s: Snapshot): void {
    this.serverTick = s.tick;
    const now = performance.now();
    for (const v of s.u) {
      const backlog = this.staticBacklog.get(v.id);
      if (backlog) {
        if (v.n === undefined) v.n = backlog.n;
        if (v.d === undefined) v.d = backlog.d;
        if (v.h === undefined) v.h = backlog.h;
        if (v.el === undefined) v.el = backlog.el;
        if (v.sz === undefined) v.sz = backlog.sz;
        if (v.pt === undefined) v.pt = backlog.pt;
        this.staticBacklog.delete(v.id);
      }
      let u = this.units.get(v.id);
      // st is authoritative per snapshot for a visible unit; a missing st means no statuses now
      const form = this.formOf(v);
      if (!u) u = this.createUnit(v, form);
      else if ((v.h !== undefined && v.h !== u.view.h) || (v.d !== undefined && v.d !== u.view.d) || form !== u.form) {
        const keep = { x: u.x, y: u.y };
        this.destroyUnit(u);
        this.units.delete(v.id);
        u = this.createUnit({ ...u.view, ...v }, form);
        u.x = keep.x;
        u.y = keep.y;
        u.seen = now;
      }
      const prev = u.view;
      u.view = { ...prev, ...v, st: v.st, n: v.n ?? prev.n, d: v.d ?? prev.d, h: v.h ?? prev.h, el: v.el ?? prev.el, af: v.af ?? prev.af, sz: v.sz ?? prev.sz, ow: v.ow ?? prev.ow, lv: v.lv ?? prev.lv, w: v.w ?? prev.w, pt: v.pt ?? prev.pt };
      u.tx = v.x;
      u.ty = v.y;
      if (u.seen === 0) {
        u.x = v.x;
        u.y = v.y;
        u.px = v.x;
        u.py = v.y;
      }
      u.seen = now;
      if (v.id === this.myId) this.reconcile(s.me);
      this.refreshUnit(u);
    }
    for (const id of s.gone) {
      const u = this.units.get(id);
      if (u) {
        this.destroyUnit(u);
        this.units.delete(id);
      }
    }
    // zones
    const seenZ = new Set<number>();
    for (const z of s.z) {
      seenZ.add(z.id);
      let zz = this.zones.get(z.id);
      const color = ZONE_COLORS[z.v] ?? 0x9b8cff;
      if (!zz) {
        const soft = new Sprite(zoneTexture());
        soft.anchor.set(0.5);
        soft.width = z.r * 2.2;
        soft.height = z.r * 2.2;
        soft.tint = color;
        soft.alpha = 0.6;
        this.layers.zones.addChild(soft);
        const g = new Graphics();
        // rune ring: dashed
        const segs = 18;
        for (let i = 0; i < segs; i++) {
          const a0 = (i / segs) * Math.PI * 2;
          const a1 = a0 + (Math.PI * 2) / segs * 0.55;
          g.arc(0, 0, z.r, a0, a1).stroke({ color, width: 2, alpha: 0.8 });
          g.closePath();
        }
        this.layers.zones.addChild(g);
        const glow = this.makeLight(z.r * 1.5, color, 0.3);
        zz = { g, glow, soft, until: z.until, color, r: z.r };
        this.zones.set(z.id, zz);
      }
      zz.g.position.set(z.x, z.y);
      zz.glow.position.set(z.x, z.y);
      zz.soft.position.set(z.x, z.y);
      zz.until = z.until;
    }
    for (const [id, zz] of this.zones) {
      if (!seenZ.has(id)) {
        zz.g.destroy();
        zz.glow.destroy();
        zz.soft.destroy();
        this.zones.delete(id);
      }
    }
    // pickups
    const seenP = new Set<number>();
    for (const p of s.pk) {
      seenP.add(p.id);
      let rec = this.pickups.get(p.id);
      if (!rec) {
        const rar = p.rar ?? 0;
        const color = p.ty === 'item' ? RARITY_COLORS[rar] : p.ty === 'heal' ? '#ff5c7a' : '#7be0c8';
        const sp = new Sprite(pickupTex(p.ty === 'item' ? 'item' : p.ty === 'heal' ? 'heal' : 'consumable', color));
        sp.anchor.set(0.5, 1);
        sp.scale.set(1 / RES);
        sp.position.set(p.x, p.y);
        sp.zIndex = p.y - 6;
        this.layers.units.addChild(sp);
        const light = p.ty === 'item' && rar > 0 ? this.makeLight(rar === 2 ? 110 : 70, hex(color), 0.35) : null;
        light?.position.set(p.x, p.y - 8);
        rec = { s: sp, y: p.y, light };
        this.pickups.set(p.id, rec);
      }
    }
    for (const [id, rec] of this.pickups) {
      if (!seenP.has(id)) {
        rec.s.destroy();
        rec.light?.destroy();
        this.pickups.delete(id);
      }
    }
    // interactables
    const seenI = new Set<number>();
    for (const it of s.it) {
      seenI.add(it.id);
      let rec = this.inters.get(it.id);
      if (!rec) {
        const accent = it.ty === 'shrine' ? '#ffe38a' : it.ty === 'chest' ? '#ffd166' : '#ff6a5a';
        const sp = new Sprite(interTex(it.ty as 'shrine' | 'chest' | 'altar'));
        sp.anchor.set(0.5, 0.95);
        sp.scale.set(1.4 / RES);
        sp.position.set(it.x, it.y);
        sp.zIndex = it.y - 4;
        this.layers.units.addChild(sp);
        const light = this.makeLight(it.ty === 'shrine' ? 200 : it.ty === 'chest' ? 140 : 170, hex(accent), 0.35);
        light.position.set(it.x, it.y - 14);
        rec = { s: sp, light };
        this.inters.set(it.id, rec);
      }
      rec.s.alpha = it.used ? 0.45 : 1;
      rec.light.alpha = it.used ? 0.06 : 0.35;
    }
    for (const [id, rec] of this.inters) {
      if (!seenI.has(id)) {
        rec.s.destroy();
        rec.light.destroy();
        this.inters.delete(id);
      }
    }
    if (s.me) this.self = s.me;
  }

  /** Transformation carried by a unit's statuses (Leomorph forms). */
  private formOf(v: UnitView): string {
    if (v.k !== 0 && v.k !== 3) return '';
    const st = v.st;
    if (!st) return '';
    return st.includes('tiger') ? 'tiger' : st.includes('bear') ? 'bear' : '';
  }

  private createUnit(v: UnitView, form = ''): UnitSprite {
    const root = new Container();
    const fig = new Container();
    const hud = new Container();
    const shadow = new Sprite(shadowTex());
    shadow.anchor.set(0.5);
    shadow.scale.set(1 / RES);
    this.layers.units.addChild(shadow);
    root.addChild(fig, hud);
    let color = '#ffffff';
    let light: Sprite | null = null;
    let art: UnitArt;
    if (v.k === 0 || v.k === 3) {
      art = buildHero((v.h ?? 'revenant') as HeroId, v.k === 3, form);
      color = v.k === 3 ? '#c8b8ff' : '#ffd2a0';
      light = this.makeLight(v.k === 3 ? 220 : v.id === this.myId ? 340 : 260, v.k === 3 ? 0xa070ff : 0xffd2a0, v.k === 3 ? 0.3 : v.id === this.myId ? 0.34 : 0.22);
    } else {
      art = buildMob(v.d ?? 'ghoul', !!v.el);
      color = '#ffffff';
      if (v.k === 2) light = this.makeLight(480, 0xff4030, 0.38);
      else if (v.el) light = this.makeLight(160, 0xffc080, 0.2);
    }
    const parts: { spec: PartSpec; s: Sprite }[] = [];
    // parts live in an inner container so a per-art scale does not fight the facing/squash animation on `fig`
    const inner = new Container();
    inner.scale.set(art.scale ?? 1);
    fig.addChild(inner);
    for (const spec of [...art.parts].sort((a, b) => a.z - b.z)) {
      const s = new Sprite(spec.tex);
      s.anchor.set(spec.ax, spec.ay);
      s.scale.set(1 / RES);
      s.position.set(spec.x, spec.y);
      if (spec.alpha !== undefined) s.alpha = spec.alpha;
      if (spec.additive) s.blendMode = 'add';
      if (spec.kind === 'wingR') s.scale.x = -1 / RES;
      inner.addChild(s);
      parts.push({ spec, s });
    }
    shadow.width = art.height * 0.55 + 8;
    shadow.height = art.height * 0.16 + 3;
    const bar = new Graphics();
    const status = new Graphics();
    const windup = new Graphics();
    hud.addChild(windup, bar, status);
    let name: Text | null = null;
    if ((v.k === 0 || v.k === 3 || (v.k === 1 && v.d !== v.n)) && v.n) {
      name = new Text({ text: v.n, style: { fontFamily: 'system-ui, Segoe UI, sans-serif', fontSize: v.k === 1 ? 11 : 12, fill: v.k === 3 ? '#c8b8ff' : v.k === 1 ? '#ff9ae8' : '#e8dcc0', stroke: { color: '#000000', width: 3 } } });
      name.anchor.set(0.5, 1);
      name.resolution = 2;
      hud.addChild(name);
    }
    const u: UnitSprite = { id: v.id, view: v, root, fig, hud, parts, art, shadow, bar, name, status, windup, light, bubble: null, x: v.x, y: v.y, tx: v.x, ty: v.y, px: v.x, py: v.y, anim: 0, attack: 0, lastHp: -1, lastMhp: -1, lastSh: -1, flash: 0, seen: 0, color, form };
    this.layers.units.addChild(root);
    this.units.set(v.id, u);
    return u;
  }

  private refreshUnit(u: UnitSprite): void {
    const v = u.view;
    const h = u.art.height;
    if (v.pt) {
      u.bar.clear();
      u.status.clear();
      return;
    }
    if (v.hp !== u.lastHp || v.mhp !== u.lastMhp || v.sh !== u.lastSh) {
      u.lastHp = v.hp;
      u.lastMhp = v.mhp;
      u.lastSh = v.sh;
      const w = v.k === 2 ? 80 : v.k === 0 || v.k === 3 ? 36 : 28;
      const y = -h - 8;
      u.bar.clear();
      u.bar.roundRect(-w / 2, y, w, 5, 2).fill({ color: 0x000000, alpha: 0.7 });
      const frac = Math.max(0, Math.min(1, v.hp / Math.max(1, v.mhp)));
      const col = v.t === this.myTeam ? (v.k === 4 ? 0x8a7cff : 0x5fd18a) : v.k === 2 ? 0xff5c5c : v.k === 3 ? 0xc65cff : 0xd8474f;
      u.bar.roundRect(-w / 2 + 0.5, y + 0.5, (w - 1) * frac, 4, 2).fill(col);
      if (v.sh > 0) u.bar.rect(-w / 2 + 0.5, y - 2, (w - 1) * Math.min(1, v.sh / Math.max(1, v.mhp)), 2).fill(0xdfe9ff);
    }
    u.status.clear();
    if (v.st && v.st.length) {
      const y = -h - 15;
      const n = Math.min(6, v.st.length);
      for (let i = 0; i < n; i++) {
        const c = STATUS_COLORS[v.st[i]] ?? 0xffffff;
        u.status.circle((i - (n - 1) / 2) * 6, y, 2.2).fill(c);
      }
    }
    u.windup.clear();
    if (v.wu !== undefined && v.wu > 0) {
      u.windup.moveTo(0, 0).arc(0, 0, 14, -Math.PI / 2, -Math.PI / 2 + Math.PI * 2 * v.wu).fill({ color: 0xff5c5c, alpha: 0.35 });
    }
    if (u.name) u.name.position.set(0, -h - 17 - (v.st?.length ? 6 : 0));
    if (u.bubble) u.bubble.c.position.set(0, -h - 34 - (v.st?.length ? 6 : 0));
  }

  showBubble(id: number, text: string): void {
    const u = this.units.get(id);
    if (!u) return;
    if (u.bubble) u.bubble.c.destroy({ children: true });
    const c = new Container();
    const t = new Text({ text: text.slice(0, 120), style: { fontFamily: 'system-ui, Segoe UI, sans-serif', fontSize: 12, fill: '#f4f1ea', wordWrap: true, wordWrapWidth: 170, align: 'center' } });
    t.resolution = 2;
    t.anchor.set(0.5, 1);
    const pad = 5;
    const bg = new Graphics().roundRect(-t.width / 2 - pad, -t.height - pad, t.width + pad * 2, t.height + pad * 2, 4).fill({ color: 0x0b0a10, alpha: 0.88 }).stroke({ color: 0x8a7a5a, width: 1 });
    bg.moveTo(-4, 0).lineTo(4, 0).lineTo(0, 5).closePath().fill({ color: 0x0b0a10, alpha: 0.88 });
    c.addChild(bg, t);
    u.hud.addChild(c);
    c.position.set(0, -u.art.height - 34 - (u.view.st?.length ? 6 : 0));
    u.bubble = { c, until: performance.now() + 5500 };
  }

  // ------------------------------------------------------------------ prediction

  private moveSpeed(): number {
    const s = this.self;
    if (!s) return 190;
    let m = 1;
    for (const st of s.st) {
      const def = STATUS[st.id as keyof typeof STATUS];
      if (def?.speedMul !== undefined) {
        if (def.speedMul === 0) return 0;
        m *= Math.pow(def.speedMul, st.stacks);
      }
    }
    return s.stats.spd * m;
  }

  private applyMove(pos: { x: number; y: number }, mx: number, my: number, dash: boolean): void {
    if (!this.map) return;
    let vx = 0;
    let vy = 0;
    const len = Math.hypot(mx, my);
    if (len > 1) {
      mx /= len;
      my /= len;
    }
    if (dash && this.dashLeft <= 0 && this.self && this.self.dash.charges > 0) {
      this.dashLeft = 5;
      if (len > 0.05) this.dashDir = { x: mx, y: my };
      else this.dashDir = { x: Math.cos(this.aimAngle), y: Math.sin(this.aimAngle) };
    }
    if (this.dashLeft > 0) {
      this.dashLeft--;
      vx = this.dashDir.x * 620;
      vy = this.dashDir.y * 620;
    } else if (len > 0.05) {
      const sp = this.moveSpeed();
      vx = mx * sp;
      vy = my * sp;
    }
    const nx = pos.x + vx / TPS;
    const ny = pos.y + vy / TPS;
    const r = this.map.resolveCircle(nx, ny, 14);
    pos.x = r.x;
    pos.y = r.y;
  }

  predict(seq: number, mx: number, my: number, dash: boolean): void {
    this.pending.push({ seq, mx, my, dash });
    if (this.pending.length > 60) this.pending.shift();
    this.applyMove(this.pred, mx, my, dash);
  }

  private reconcile(me?: SelfView): void {
    if (!me) return;
    const err = Math.hypot(me.x - this.pred.x, me.y - this.pred.y);
    if (me.dead) {
      this.pred = { x: me.x, y: me.y };
      this.pending = [];
      return;
    }
    this.pending = this.pending.filter((p) => p.seq > me.seq);
    if (err > 2) {
      const pos = { x: me.x, y: me.y };
      const savedDash = this.dashLeft;
      for (const p of this.pending) this.applyMove(pos, p.mx, p.my, false);
      this.dashLeft = savedDash;
      if (err > 120) {
        this.pred = pos;
        this.renderSelf = { ...pos };
      } else {
        this.pred.x += (pos.x - this.pred.x) * 0.5;
        this.pred.y += (pos.y - this.pred.y) * 0.5;
      }
    }
  }

  /** Nearest live enemy to the hero, for touch auto-aim. */
  nearestEnemyPos(maxDist = 900): { x: number; y: number } | null {
    let best: { x: number; y: number } | null = null;
    let bestD = maxDist * maxDist;
    for (const u of this.units.values()) {
      const v = u.view;
      if (u.id === this.myId || v.pt || v.hp <= 0) continue;
      if (v.t === this.myTeam) continue;
      const dx = u.x - this.pred.x;
      const dy = u.y - this.pred.y;
      const d = dx * dx + dy * dy;
      if (d < bestD) {
        bestD = d;
        best = { x: u.x, y: u.y };
      }
    }
    return best;
  }

  screenToWorld(sx: number, sy: number): { x: number; y: number } {
    const w = this.app.screen.width;
    const h = this.app.screen.height;
    return { x: (sx - w / 2) / this.zoom + this.cam.x, y: (sy - h / 2) / this.zoom + this.cam.y };
  }

  // ------------------------------------------------------------------ events

  applyEvents(evs: SimEvent[]): void {
    const now = performance.now();
    for (const e of evs) {
      switch (e.t) {
        case 'proj': {
          const st = projStyle(e.visual);
          const sprite = new Sprite(projTexture(e.visual));
          sprite.anchor.set(0.5);
          sprite.position.set(e.x, e.y);
          sprite.rotation = Math.atan2(e.vy, e.vx);
          if (e.team !== 0 && e.team !== this.myTeam) sprite.alpha = 0.85;
          else if (e.team === this.myTeam && e.owner !== this.myId && prefs.value.dimOthers) sprite.alpha = 0.45;
          this.layers.proj.addChild(sprite);
          const ticks = Math.max(1, e.until - this.serverTick);
          const pr: ProjSprite = { id: e.id, sprite, x: e.x, y: e.y, vx: e.vx, vy: e.vy, dieAt: now + (ticks / TPS) * 1000 + 60, trail: !!st.trail, color: hex(st.glow ?? st.color) };
          if (e.orbit) pr.orbit = { owner: e.owner, radius: e.orbit.radius, angVel: e.orbit.angVel, angle: Math.atan2(e.y - (this.units.get(e.owner)?.y ?? e.y), e.x - (this.units.get(e.owner)?.x ?? e.x)) };
          this.projs.set(e.id, pr);
          const owner = this.units.get(e.owner);
          if (owner && !e.orbit) owner.attack = 1;
          if (e.owner === this.myId) this.onSfx(projSfx(e.visual, st.shape));
          else if (!e.orbit) this.onSfx(projSfx(e.visual, st.shape), { x: e.x, y: e.y, vol: e.team === this.myTeam ? 0.45 : 0.7, throttleMs: 70 });
          break;
        }
        case 'projEnd': {
          const p = this.projs.get(e.id);
          if (p) {
            p.sprite.destroy();
            this.projs.delete(e.id);
          }
          break;
        }
        case 'hit': {
          const u = this.units.get(e.id);
          if (u) u.flash = e.heal ? 0 : 0.09;
          if (prefs.value.dmgNumbers) this.damageNumber(e.x, e.y - 24, e.heal ? '+' + e.dmg : String(e.dmg), e.heal ? 0x7be0a0 : e.crit ? 0xffd166 : e.team === this.myTeam || e.team === -1 ? 0xffffff : 0xff6a6a, e.crit ? 1.35 : 1);
          if (!e.heal) this.burst(e.x, e.y - 10, e.crit ? 10 : 4, e.crit ? 0xffd166 : 0xffe0c0, 110, 0.3);
          if (e.id === this.myId && !e.heal) {
            this.onSfx('hurt');
            this.addShake(Math.min(6, e.dmg / 12));
          } else if (!e.heal) {
            const ally = e.team === this.myTeam;
            this.onSfx(e.crit ? 'hit_crit' : 'hit', { x: e.x, y: e.y, vol: ally ? 1 : 0.55, throttleMs: 60 });
            if (ally && e.tag && ELEMENT_TAGS.has(e.tag)) this.onSfx('hit_' + e.tag, { x: e.x, y: e.y, vol: 0.8, throttleMs: 90 });
          }
          if (e.heal && e.id === this.myId) this.onSfx('heal');
          else if (e.heal) this.onSfx('heal', { x: e.x, y: e.y, vol: 0.4, throttleMs: 300 });
          break;
        }
        case 'death': {
          const col = e.kind === 'player' ? 0xffd166 : e.kind === 'boss' ? 0xff5c5c : e.kind === 'revenant' ? 0xc65cff : e.kind === 'summon' ? 0x8a7cff : 0xc0304f;
          this.burst(e.x, e.y - 8, e.kind === 'boss' ? 70 : e.kind === 'player' ? 30 : 14, col, e.kind === 'boss' ? 280 : 160, e.kind === 'boss' ? 1.2 : 0.7, true);
          this.ring(e.x, e.y, e.kind === 'boss' ? 240 : 40, col, 0.5, false);
          if (e.kind !== 'summon' && e.kind !== 'player') this.decal(e.x, e.y, e.kind === 'boss' ? 2.6 : 1, e.kind === 'revenant' ? '#2a1040' : '#3a0f18');
          if (e.kind === 'player') {
            if (e.id === this.myId) {
              this.onSfx('death');
              this.addShake(10);
            } else this.onSfx('death', { x: e.x, y: e.y, vol: 0.5 });
          } else if (e.kind === 'boss') {
            this.onSfx('explosion', { vol: 1.5 });
            this.onSfx('boss', { vol: 0.8 });
            this.addShake(14);
          } else if (e.kind === 'revenant') this.onSfx('revdeath', { x: e.x, y: e.y });
          else this.onSfx('mobdeath', { x: e.x, y: e.y, vol: e.kind === 'summon' ? 0.4 : 1, throttleMs: 70 });
          break;
        }
        case 'fx':
          this.fx(e.kind, e.x, e.y, e.r, e.a, e.x2, e.y2, e.id);
          break;
        case 'levelup':
          this.ring(e.x, e.y, 60, 0xffd166, 0.6, true);
          this.burst(e.x, e.y - 20, 24, 0xffd166, 170, 0.9);
          this.flashLight(e.x, e.y, 260, 0xffd166, 0.7);
          if (e.id === this.myId) this.onSfx('levelup');
          else this.onSfx('levelup', { x: e.x, y: e.y, vol: 0.35, throttleMs: 300 });
          break;
        case 'cast': {
          const u = this.units.get(e.id);
          if (u) u.attack = 1;
          const kind = castSfx(e.skill);
          if (e.id === this.myId) this.onSfx(kind, { throttleMs: 60 });
          else this.onSfx(kind, { x: e.x, y: e.y, vol: 0.6, throttleMs: 120 });
          break;
        }
        case 'telegraph': {
          const g = new Graphics();
          this.layers.zones.addChild(g);
          const ticks = Math.max(1, e.until - this.serverTick);
          this.telegraphs.push({ g, x: e.x, y: e.y, r: e.r, x2: e.x2, y2: e.y2, kind: e.kind, dieAt: now + (ticks / TPS) * 1000, bornAt: now });
          break;
        }
        case 'shake':
          this.addShake(6 * e.power);
          if (e.power >= 1) this.onSfx('boss', { throttleMs: 500 });
          break;
        case 'pickup':
          if (e.id === this.myId) this.onSfx('pickup');
          break;
        default:
          break;
      }
    }
  }

  private fx(kind: string, x: number, y: number, r?: number, a?: number, x2?: number, y2?: number, id?: number): void {
    const color = FX_COLORS[kind] ?? 0x9b8cff;
    if (kind.startsWith('swing') || kind === 'bash') {
      this.slash(x, y - 14, r ?? 90, a ?? 0, color, kind === 'swing_fast' ? 0.11 : kind === 'swing_boss' ? 0.22 : 0.16);
      const u = id !== undefined ? this.units.get(id) : undefined;
      if (u) u.attack = 1;
      this.onSfx(SWING_SFX[kind] ?? 'swing', id === this.myId ? { throttleMs: 60 } : { x, y, vol: 0.8, throttleMs: 80 });
      return;
    }
    if (kind === 'dash' || kind === 'charge') {
      if (id !== undefined) this.afterimage(id, a ?? 0);
      if (x2 !== undefined && y2 !== undefined) {
        const g = new Graphics().moveTo(x, y).lineTo(x2, y2).stroke({ color, width: 8, alpha: 0.3 });
        this.layers.fx.addChild(g);
        this.lines.push({ g, life: 0.2, max: 0.2, from: 1 });
      }
      this.onSfx('dash', { x, y, throttleMs: 100 });
      return;
    }
    if (kind === 'chain' || kind === 'chain_big' || kind === 'blink') {
      if (x2 !== undefined && y2 !== undefined) {
        const g = new Graphics();
        const width = kind === 'chain_big' ? 5 : kind === 'chain' ? 3 : 6;
        if (kind !== 'blink') {
          const segs = 6;
          g.moveTo(x, y);
          for (let i = 1; i < segs; i++) {
            const t = i / segs;
            g.lineTo(x + (x2 - x) * t + (Math.random() - 0.5) * 16, y + (y2 - y) * t + (Math.random() - 0.5) * 16);
          }
          g.lineTo(x2, y2).stroke({ color, width, alpha: 0.9 });
          this.burst(x2, y2, 5, color, 90, 0.25);
          this.flashLight(x2, y2, 90, color, 0.25);
        } else {
          g.moveTo(x, y).lineTo(x2, y2).stroke({ color, width, alpha: 0.35 });
          this.burst(x, y, 8, color, 80, 0.4);
          this.burst(x2, y2, 8, color, 80, 0.4);
        }
        this.layers.fx.addChild(g);
        this.lines.push({ g, life: kind === 'blink' ? 0.3 : 0.2, max: kind === 'blink' ? 0.3 : 0.2, from: 1 });
      }
      this.onSfx(kind === 'blink' ? 'cast_void' : 'zap', { x, y, vol: kind === 'blink' ? 0.5 : 0.7, throttleMs: 80 });
      return;
    }
    if (kind.startsWith('explosion')) {
      this.ring(x, y, r ?? 70, color, 0.35, true);
      this.burst(x, y, kind.endsWith('big') ? 44 : 20, color, 240, 0.65, true);
      this.flashLight(x, y, (r ?? 70) * 3, color, 0.6);
      this.addShake(kind.endsWith('big') ? 5 : 2);
      this.onSfx('explosion', { x, y, vol: kind.endsWith('big') ? 1.3 : 1, throttleMs: 120 });
      if (kind.includes('fire')) this.onSfx('hit_fire', { x, y, throttleMs: 120 });
      return;
    }
    if (kind === 'quake') {
      this.ring(x, y, r ?? 190, color, 0.5, true);
      this.addShake(8);
      this.onSfx('swing_boss', { x, y, throttleMs: 200 });
      this.onSfx('explosion', { x, y, vol: 0.6, throttleMs: 200 });
      return;
    }
    if (kind === 'ward' || kind === 'heal' || kind === 'haste' || kind === 'cry' || kind === 'terror' || kind === 'aura_buff' || kind === 'aura_curse') {
      this.ring(x, y, r ?? 120, color, 0.5, false);
      this.burst(x, y - 10, 12, color, 90, 0.7);
      if (kind === 'ward' || kind === 'haste') this.onSfx('shield', { x, y, throttleMs: 200 });
      else if (kind === 'heal') this.onSfx('heal', { x, y, vol: 0.6, throttleMs: 200 });
      return;
    }
    if (kind === 'summon') {
      this.ring(x, y, 30, color, 0.4, true);
      this.burst(x, y, 10, color, 70, 0.5);
      this.flashLight(x, y, 120, color, 0.4);
      this.onSfx('cast_summon', { x, y, vol: 0.5, throttleMs: 150 });
      return;
    }
    if (kind === 'pick') {
      this.ring(x, y, 40, color, 0.4, false);
      this.onSfx('pick');
      return;
    }
    if (kind === 'shrine' || kind === 'chest' || kind === 'altar') {
      this.ring(x, y, r ?? 60, color, 0.6, true);
      this.burst(x, y - 10, 18, color, 130, 0.9);
      this.flashLight(x, y, 260, color, 0.8);
      this.onSfx(kind, { x, y });
      return;
    }
    this.ring(x, y, r ?? 60, color, 0.35, true);
    if (kind.startsWith('nova') || kind.startsWith('pulse')) this.burst(x, y, 12, color, 150, 0.45);
  }

  /** Animated crescent slash with sparks along the blade edge. */
  private slash(x: number, y: number, reach: number, ang: number, color: number, dur: number): void {
    const tex = slashTex();
    const main = new Sprite(tex);
    main.anchor.set(0.5);
    main.blendMode = 'add';
    main.tint = color;
    main.position.set(x, y);
    const trail = new Sprite(tex);
    trail.anchor.set(0.5);
    trail.blendMode = 'add';
    trail.tint = color;
    trail.position.set(x, y);
    this.layers.fx.addChild(trail, main);
    this.slashDir = -this.slashDir;
    this.slashes.push({ main, trail, reach, ang, dir: this.slashDir, life: dur, max: dur });
    // sparks along the arc
    const n = 7;
    for (let i = 0; i < n; i++) {
      const sa = ang + (i / (n - 1) - 0.5) * 1.6;
      const px = x + Math.cos(sa) * reach * 0.9;
      const py = y + Math.sin(sa) * reach * 0.9;
      const s = new Sprite(circleTexture(2, '#ffffff'));
      s.anchor.set(0.5);
      s.tint = color;
      s.blendMode = 'add';
      s.position.set(px, py);
      s.scale.set(0.8 + Math.random() * 0.8);
      this.layers.fx.addChild(s);
      const v = 90 + Math.random() * 120;
      this.particles.push({ g: s, x: px, y: py, vx: Math.cos(sa) * v, vy: Math.sin(sa) * v - 30, life: 0.28, max: 0.28, grav: 160, shrink: true });
    }
  }

  private afterimage(id: number, angle: number): void {
    const u = this.units.get(id);
    if (!u) return;
    for (let i = 1; i <= 3; i++) {
      const c = new Container();
      for (const p of u.parts) {
        const s = new Sprite(p.s.texture);
        s.anchor.set(p.spec.ax, p.spec.ay);
        s.scale.set(p.s.scale.x, p.s.scale.y);
        s.position.set(p.s.x, p.s.y);
        s.rotation = p.s.rotation;
        s.tint = 0xa0c8ff;
        c.addChild(s);
      }
      const k = u.art.scale ?? 1;
      c.scale.set(u.fig.scale.x * k, k);
      c.position.set(u.x - Math.cos(angle) * i * 22, u.y - Math.sin(angle) * i * 22);
      c.alpha = 0.5 - i * 0.12;
      c.zIndex = u.y - 1;
      this.layers.units.addChild(c);
      this.afterimages.push({ g: c, life: 0.28, max: 0.28, from: c.alpha });
    }
  }

  private flashLight(x: number, y: number, radius: number, color: number, life: number): void {
    const l = this.makeLight(radius, color, 0.5);
    l.position.set(x, y);
    this.afterimages.push({ g: l, life, max: life, from: 0.5 });
  }

  private decal(x: number, y: number, scale: number, color: string): void {
    if (this.decals.length > 80) {
      const old = this.decals.shift()!;
      old.g.destroy();
    }
    const s = new Sprite(decalTexture(Math.floor(Math.random() * 4), color));
    s.anchor.set(0.5);
    s.rotation = Math.random() * Math.PI * 2;
    s.scale.set(scale);
    s.position.set(x, y);
    s.alpha = 0.7;
    this.layers.decor.addChild(s);
    this.decals.push({ g: s, life: 14, max: 14, from: 0.7 });
  }

  private ring(x: number, y: number, r: number, color: number, dur: number, fill: boolean): void {
    const g = new Graphics();
    g.position.set(x, y);
    this.layers.fx.addChild(g);
    this.rings.push({ g, x, y, r, life: dur, max: dur, color, fill });
  }

  private burst(x: number, y: number, n: number, color: number, speed: number, life: number, chunks = false): void {
    if (this.particles.length > 900) return;
    const tex = circleTexture(3, '#ffffff');
    for (let i = 0; i < n; i++) {
      const s = new Sprite(tex);
      s.anchor.set(0.5);
      s.tint = color;
      s.position.set(x, y);
      const a = Math.random() * Math.PI * 2;
      const v = speed * (0.4 + Math.random() * 0.8);
      const l = life * (0.6 + Math.random() * 0.8);
      s.scale.set((chunks ? 0.9 : 0.6) + Math.random() * 0.8);
      if (!chunks) s.blendMode = 'add';
      this.layers.fx.addChild(s);
      this.particles.push({ g: s, x, y, vx: Math.cos(a) * v, vy: Math.sin(a) * v - (chunks ? 60 : 0), life: l, max: l, grav: chunks ? 220 : 60, shrink: chunks });
    }
  }

  private trail(x: number, y: number, color: number): void {
    if (this.particles.length > 900) return;
    const s = new Sprite(circleTexture(3, '#ffffff'));
    s.anchor.set(0.5);
    s.tint = color;
    s.alpha = 0.55;
    s.blendMode = 'add';
    s.position.set(x, y);
    s.scale.set(0.9);
    this.layers.fx.addChild(s);
    this.particles.push({ g: s, x, y, vx: 0, vy: 0, life: 0.14, max: 0.14, grav: 0, shrink: true });
  }

  private damageNumber(x: number, y: number, text: string, color: number, scale: number): void {
    if (this.dmg.length > 80) {
      const old = this.dmg.shift()!;
      old.t.destroy();
    }
    let t: BitmapText;
    try {
      t = new BitmapText({ text, style: { fontFamily: 'dmg', fontSize: 16 * scale } });
    } catch {
      return;
    }
    t.tint = color;
    t.anchor.set(0.5);
    t.position.set(x + (Math.random() - 0.5) * 16, y);
    this.layers.text.addChild(t);
    this.dmg.push({ t, x: t.x, y, vy: -60, life: 0.8, max: 0.8 });
  }

  addShake(power: number): void {
    if (!prefs.value.shake) return;
    this.shake.power = Math.min(18, this.shake.power + power);
  }

  // ------------------------------------------------------------------ frame

  private animateUnit(u: UnitSprite, dt: number, now: number): void {
    const speed = Math.hypot(u.x - u.px, u.y - u.py) / Math.max(0.001, dt);
    u.px = u.x;
    u.py = u.y;
    const moving = speed > 25;
    if (moving) u.anim += dt * Math.min(16, 7 + speed / 40);
    else u.anim += (Math.round(u.anim / Math.PI) * Math.PI - u.anim) * Math.min(1, dt * 12);
    u.attack = Math.max(0, u.attack - dt * 4);
    const t = now / 1000;
    const fam = u.art.family;
    const hop = moving && fam === 'walker' ? Math.abs(Math.sin(u.anim)) * 2.5 : 0;
    const breathe = Math.sin(t * 2.2 + u.id) * 0.6;
    const swing = Math.sin(u.anim);
    for (const { spec, s } of u.parts) {
      switch (spec.kind) {
        case 'legL':
          s.rotation = moving ? swing * 0.55 : 0;
          break;
        case 'legR':
          s.rotation = moving ? -swing * 0.55 : 0;
          break;
        case 'body':
          if (fam === 'walker') {
            s.y = spec.y - hop + breathe * 0.4;
            s.rotation = moving ? swing * 0.06 : 0;
          } else if (fam === 'blob') {
            const sq = Math.sin(t * 5 + u.id) * 0.06 + (moving ? Math.abs(swing) * 0.08 : 0);
            s.scale.set((1 / RES) * (1 + sq), (1 / RES) * (1 - sq));
          } else if (fam === 'spirit') {
            s.y = spec.y + Math.sin(t * 2.4 + u.id) * 3;
            s.alpha = 0.78 + Math.sin(t * 3.1 + u.id) * 0.12;
            s.rotation = Math.sin(t * 1.7 + u.id) * 0.06;
          } else if (fam === 'flyer') {
            s.y = spec.y + Math.sin(t * 4 + u.id) * 3;
          } else if (fam === 'beast') {
            s.y = spec.y - (moving ? Math.abs(swing) * 2 : 0) + breathe * 0.3;
            s.rotation = moving ? swing * 0.05 : 0;
          } else {
            s.scale.set((1 / RES) * (1 + Math.sin(t * 2 + u.id) * 0.02));
          }
          break;
        case 'head':
          s.y = spec.y - hop * 0.9 + breathe * 0.35;
          s.rotation = moving ? -swing * 0.04 : 0;
          break;
        case 'weapon':
          s.y = spec.y - hop * 0.7 + breathe * 0.3;
          s.rotation = -u.attack * u.attack * 1.7 + (moving ? swing * 0.08 : 0);
          break;
        case 'wingL':
          s.rotation = Math.sin(t * 16 + u.id) * 0.55;
          s.y = spec.y + Math.sin(t * 4 + u.id) * 3;
          break;
        case 'wingR':
          s.rotation = -Math.sin(t * 16 + u.id) * 0.55;
          s.y = spec.y + Math.sin(t * 4 + u.id) * 3;
          break;
        default:
          break;
      }
      if (u.flash > 0) s.tint = 0xff7070;
      else if (s.tint === 0xff7070) s.tint = 0xffffff;
    }
    if (u.flash > 0) u.flash -= dt;
    // facing
    const face = Math.cos(u.view.f) < 0 ? -1 : 1;
    u.fig.scale.x += (face - u.fig.scale.x) * Math.min(1, dt * 18);
    if (u.attack > 0.85) u.fig.scale.set(u.fig.scale.x * 1.06, 0.94);
    else u.fig.scale.y += (1 - u.fig.scale.y) * Math.min(1, dt * 14);
    if (u.view.pt) u.fig.y = Math.sin(now / 160 + u.id) * 1.5;
    u.shadow.position.set(u.x, u.y);
    u.shadow.zIndex = u.y - 3;
    u.shadow.alpha = fam === 'spirit' || fam === 'flyer' ? 0.5 : 0.9;
    u.shadow.scale.set((1 / RES) * (1 - hop * 0.04));
  }

  private updateAtmosphere(dt: number, now: number): void {
    if (!this.map) return;
    const tier = this.map.kind === 'realm' ? this.map.tierAt(this.cam.x, this.cam.y) : 2;
    this.curTier = tier;
    const pal = TIER_PALETTES[tier] ?? TIER_PALETTES[1];
    this.gradeColor = lerpColor(this.gradeColor, pal.grade, Math.min(1, dt * 1.5));
    if (this.grade) this.grade.tint = this.gradeColor;
    const w = this.app.screen.width / this.zoom;
    const h = this.app.screen.height / this.zoom;
    for (const f of this.fogs) {
      f.s.x += f.vx * dt;
      f.s.y += f.vy * dt;
      f.s.tint = lerpColor(f.s.tint, pal.fog, Math.min(1, dt));
      if (f.s.x - this.cam.x > w * 0.9 + 600) f.s.x = this.cam.x - w * 0.9 - 500;
      if (f.s.x - this.cam.x < -w * 0.9 - 700) f.s.x = this.cam.x + w * 0.9 + 500;
      if (Math.abs(f.s.y - this.cam.y) > h + 700) f.s.y = this.cam.y + (Math.random() - 0.5) * h;
    }
    for (const a of this.ambient) {
      a.phase += dt;
      const off = a.x - this.cam.x;
      const offy = a.y - this.cam.y;
      const out = Math.abs(off) > w * 0.6 + 40 || Math.abs(offy) > h * 0.6 + 40 || a.s.alpha === 0;
      if (out) {
        a.x = this.cam.x + (Math.random() - 0.5) * w * 1.1;
        a.y = this.cam.y + (Math.random() - 0.5) * h * 1.1 + (tier === 3 ? h * 0.3 : 0);
        a.vx = (Math.random() - 0.5) * (tier === 2 ? 18 : 10);
        a.vy = tier === 3 ? -18 - Math.random() * 30 : tier === 2 ? -4 - Math.random() * 8 : (Math.random() - 0.5) * 12;
        a.phase = Math.random() * 10;
        a.s.tint = pal.particle;
        a.s.scale.set(tier === 3 ? 0.6 + Math.random() * 0.6 : 0.5 + Math.random() * 0.5);
      }
      if (tier === 1) {
        a.vx += Math.sin(a.phase * 1.3 + a.s.tint) * 4 * dt;
        a.vy += Math.cos(a.phase * 1.7) * 4 * dt;
      }
      a.x += a.vx * dt;
      a.y += a.vy * dt;
      a.s.position.set(a.x, a.y);
      const blink = tier === 1 ? Math.max(0, Math.sin(a.phase * 2.5)) : tier === 3 ? 0.6 + Math.sin(a.phase * 9) * 0.35 : 0.5;
      a.s.alpha = Math.max(0.05, blink * (tier === 2 ? 0.35 : 0.85));
    }
  }

  private update(dt: number): void {
    if (!this.ready || this.destroyed) return;
    dt = Math.min(0.1, dt);
    this.frame++;
    const now = performance.now();
    const w = this.app.screen.width;
    const h = this.app.screen.height;
    this.zoom = Math.max(1, Math.min(2.4, h / 640));
    const me = this.units.get(this.myId);
    if (me) {
      this.renderSelf.x += (this.pred.x - this.renderSelf.x) * Math.min(1, dt * 22);
      this.renderSelf.y += (this.pred.y - this.renderSelf.y) * Math.min(1, dt * 22);
      me.x = this.renderSelf.x;
      me.y = this.renderSelf.y;
    }
    for (const u of this.units.values()) {
      if (u.id !== this.myId) {
        const k = Math.min(1, dt * 16);
        u.x += (u.tx - u.x) * k;
        u.y += (u.ty - u.y) * k;
      }
      this.animateUnit(u, dt, now);
      u.root.position.set(u.x, u.y);
      u.root.zIndex = u.y;
      if (u.light) u.light.position.set(u.x, u.y - 10);
      if (u.name) u.name.visible = prefs.value.names || u.view.k === 3;
      if (u.bubble && now > u.bubble.until) {
        u.bubble.c.destroy({ children: true });
        u.bubble = null;
      }
    }
    // camera
    const target = me ? this.renderSelf : this.cam;
    this.cam.x += (target.x - this.cam.x) * Math.min(1, dt * 10);
    this.cam.y += (target.y - this.cam.y) * Math.min(1, dt * 10);
    if (this.shake.power > 0) {
      this.shake.x = (Math.random() - 0.5) * this.shake.power;
      this.shake.y = (Math.random() - 0.5) * this.shake.power;
      this.shake.power = Math.max(0, this.shake.power - dt * 30);
    } else {
      this.shake.x = 0;
      this.shake.y = 0;
    }
    this.world.scale.set(this.zoom);
    this.world.position.set(w / 2 - this.cam.x * this.zoom + this.shake.x, h / 2 - this.cam.y * this.zoom + this.shake.y);
    this.updateChunks();
    this.updateAtmosphere(dt, now);
    // threat level for the adaptive score: 2 boss in view, 1 a pack or a revenant near, else 0
    if (this.frame % 6 === 0) {
      let threat = 0;
      let near = 0;
      let pressure = false;
      for (const u of this.units.values()) {
        if (u.id === this.myId || u.view.t === this.myTeam || u.view.t === 0 || u.view.hp <= 0) continue;
        const dx = u.x - this.cam.x;
        const dy = u.y - this.cam.y;
        const d2 = dx * dx + dy * dy;
        if (u.view.k === 2 && d2 < 900 * 900) threat = 2;
        else if (u.view.k === 3 && d2 < 700 * 700) pressure = true;
        else if (u.view.k === 0 && d2 < 800 * 800) pressure = true;
        else if (d2 < 450 * 450) near++;
      }
      if (near >= 3 || pressure) this.lastThreat = now;
      if (threat < 2 && now - this.lastThreat < 3500) threat = 1;
      this.onListener(this.cam.x, this.cam.y, threat, this.curTier);
    }
    // pickups bob
    for (const p of this.pickups.values()) {
      p.s.y = p.y + Math.sin(now / 300 + p.y) * 2;
    }
    // projectiles
    for (const [id, p] of this.projs) {
      if (now >= p.dieAt) {
        p.sprite.destroy();
        this.projs.delete(id);
        continue;
      }
      if (p.orbit) {
        const o = this.units.get(p.orbit.owner);
        if (!o) {
          p.sprite.destroy();
          this.projs.delete(id);
          continue;
        }
        p.orbit.angle += p.orbit.angVel * dt;
        p.x = o.x + Math.cos(p.orbit.angle) * p.orbit.radius;
        p.y = o.y - 14 + Math.sin(p.orbit.angle) * p.orbit.radius * 0.7;
        p.sprite.rotation = p.orbit.angle + Math.PI / 2;
      } else {
        p.x += p.vx * dt;
        p.y += p.vy * dt;
        if (p.trail && this.frame % 2 === 0) this.trail(p.x, p.y, p.color);
      }
      p.sprite.position.set(p.x, p.y);
    }
    for (const z of this.zones.values()) {
      z.g.rotation += dt * 0.6;
      z.g.alpha = 0.7 + Math.sin(now / 150) * 0.25;
      z.glow.alpha = 0.22 + Math.sin(now / 150) * 0.08;
      z.soft.alpha = 0.55 + Math.sin(now / 220) * 0.1;
    }
    // slashes: the crescent sweeps across the arc, stretches and fades
    for (let i = this.slashes.length - 1; i >= 0; i--) {
      const sl = this.slashes[i];
      sl.life -= dt;
      if (sl.life <= 0) {
        sl.main.destroy();
        sl.trail.destroy();
        this.slashes.splice(i, 1);
        continue;
      }
      const t = 1 - sl.life / sl.max;
      const sweep = sl.dir * (-0.5 + t * 0.9);
      const sc = (sl.reach / (52 * RES)) * (0.72 + t * 0.42);
      sl.main.rotation = sl.ang + sweep;
      sl.main.scale.set(sc, sc * (1 - t * 0.25) * sl.dir);
      sl.main.alpha = Math.pow(1 - t, 1.4) * 0.95;
      sl.trail.rotation = sl.ang + sweep - sl.dir * 0.28;
      sl.trail.scale.set(sc * 0.92, sc * 0.75 * sl.dir);
      sl.trail.alpha = Math.pow(1 - t, 2) * 0.45;
    }
    // telegraphs
    for (let i = this.telegraphs.length - 1; i >= 0; i--) {
      const t = this.telegraphs[i];
      if (now >= t.dieAt + 80) {
        t.g.destroy();
        this.telegraphs.splice(i, 1);
        continue;
      }
      const prog = Math.min(1, (now - t.bornAt) / Math.max(1, t.dieAt - t.bornAt));
      t.g.clear();
      if (t.kind === 'circle') {
        t.g.circle(t.x, t.y, t.r).stroke({ color: 0xff5c5c, width: 2, alpha: 0.8 });
        t.g.circle(t.x, t.y, t.r * prog).fill({ color: 0xff3b3b, alpha: 0.28 });
      } else if (t.x2 !== undefined && t.y2 !== undefined) {
        t.g.moveTo(t.x, t.y).lineTo(t.x2, t.y2).stroke({ color: 0xff5c5c, width: t.r * 2, alpha: 0.18 + prog * 0.2 });
        t.g.moveTo(t.x, t.y).lineTo(t.x + (t.x2 - t.x) * prog, t.y + (t.y2 - t.y) * prog).stroke({ color: 0xff3b3b, width: 3, alpha: 0.8 });
      }
    }
    // particles
    for (let i = this.particles.length - 1; i >= 0; i--) {
      const p = this.particles[i];
      p.life -= dt;
      if (p.life <= 0) {
        p.g.destroy();
        this.particles.splice(i, 1);
        continue;
      }
      p.vy += p.grav * dt;
      p.x += p.vx * dt;
      p.y += p.vy * dt;
      p.vx *= 0.96;
      p.g.position.set(p.x, p.y);
      const f = p.life / p.max;
      p.g.alpha = f;
      if (p.shrink) p.g.scale.set(Math.max(0.1, f) * 1.1);
    }
    for (let i = this.rings.length - 1; i >= 0; i--) {
      const r = this.rings[i];
      r.life -= dt;
      if (r.life <= 0) {
        r.g.destroy();
        this.rings.splice(i, 1);
        continue;
      }
      const t = 1 - r.life / r.max;
      r.g.clear();
      const rad = r.r * (0.3 + 0.7 * t);
      if (r.fill) r.g.circle(0, 0, rad).fill({ color: r.color, alpha: 0.25 * (1 - t) });
      r.g.circle(0, 0, rad).stroke({ color: r.color, width: 3, alpha: 0.9 * (1 - t) });
    }
    this.fade(this.lines, dt);
    this.fade(this.afterimages, dt);
    this.fade(this.decals, dt);
    for (let i = this.dmg.length - 1; i >= 0; i--) {
      const d = this.dmg[i];
      d.life -= dt;
      if (d.life <= 0) {
        d.t.destroy();
        this.dmg.splice(i, 1);
        continue;
      }
      d.y += d.vy * dt;
      d.vy += 40 * dt;
      d.t.position.set(d.x, d.y);
      d.t.alpha = Math.min(1, d.life / d.max + 0.3);
      d.t.scale.set(1 / this.zoom);
    }
    // overlay
    if (this.vignette) {
      this.vignette.width = w;
      this.vignette.height = h;
    }
    if (this.grade) {
      this.grade.width = w;
      this.grade.height = h;
    }
    if (this.lowHp) {
      this.lowHp.width = w;
      this.lowHp.height = h;
      const frac = this.self ? this.self.hp / Math.max(1, this.self.mhp) : 1;
      this.lowHp.alpha = this.self && !this.self.dead && frac < 0.3 ? 0.1 + (0.3 - frac) * 0.5 + Math.sin(now / 160) * 0.04 : 0;
    }
    if (this.frame % 6 === 0) this.drawMinimap(w);
  }

  private fade(list: Fading[], dt: number): void {
    for (let i = list.length - 1; i >= 0; i--) {
      const f = list[i];
      f.life -= dt;
      if (f.life <= 0) {
        f.g.destroy({ children: true });
        list.splice(i, 1);
        continue;
      }
      f.g.alpha = f.from * (f.life / f.max);
    }
  }

  private drawMinimap(w: number): void {
    const g = this.minimap;
    g.clear();
    if (!this.map || !this.showMinimap || !prefs.value.minimap) return;
    const size = 150;
    const x0 = w - size - 14;
    const y0 = 14;
    const scale = size / this.map.widthPx;
    g.roundRect(x0, y0, size, size, 4).fill({ color: 0x0b0a10, alpha: 0.75 }).roundRect(x0, y0, size, size, 4).stroke({ color: 0x8a7a5a, width: 1 });
    const c = this.map.center;
    const maxD = this.map.widthPx / 2;
    g.circle(x0 + c.x * scale, y0 + c.y * scale, maxD * 0.68 * scale).stroke({ color: 0x3a3541, width: 1 });
    g.circle(x0 + c.x * scale, y0 + c.y * scale, maxD * 0.36 * scale).stroke({ color: 0x5a3d4a, width: 1 });
    g.circle(x0 + c.x * scale, y0 + c.y * scale, this.map.arenaRadius * scale).stroke({ color: 0xc0304f, width: 1 });
    for (const it of this.inters.values()) if (it.s.alpha > 0.5) g.rect(x0 + it.s.x * scale - 1, y0 + it.s.y * scale - 1, 2, 2).fill(0x9a8f7a);
    for (const u of this.units.values()) {
      const v = u.view;
      if (v.k === 1 || v.k === 4) continue;
      const px = x0 + u.x * scale;
      const py = y0 + u.y * scale;
      if (v.k === 2) g.circle(px, py, 4).fill(0xff5c5c);
      else if (v.k === 3) g.circle(px, py, 2.5).fill(0xc65cff);
      else if (v.id === this.myId) g.circle(px, py, 3).fill(0xffd166);
      else g.circle(px, py, 2).fill(0xe8dcc0);
    }
  }
}
